import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { inject, observer } from 'mobx-react';

import { getConfig, setConfig } from '@constants';

import { EError, EStore } from '@enums';

import {
	Log,
	WindowVisibility,
} from '@utils';

import {
	AlbumsStore,
	ArtistsStore,
	PlaylistsStore,
	TracksStore,
	UsersStore,
} from '@mobx';

import { AppStat } from '../src/utils/AppStat';

import { IRouteConfigComponent } from '@interfaces';

export interface IAppBaseProps extends RouteComponentProps {

}

interface IAppRenderProps {
	error: string;
}

export interface IAppOwnProps {
	routes: IRouteConfigComponent[];
	render(renderProps: IAppRenderProps): React.ReactNode;
}

export interface IAppProps extends IAppBaseProps, IAppOwnProps {
	albumsStore: AlbumsStore;
	artistsStore: ArtistsStore;
	playlistsStore: PlaylistsStore;
	tracksStore: TracksStore;
	usersStore: UsersStore;
}

export interface IAppState {
	error: string;
}

@inject(
	EStore.ALBUMS,
	EStore.ARTISTS,
	EStore.PLAYLISTS,
	EStore.TRACKS,
	EStore.USERS,
)
@observer
class AppComponent extends React.Component<IAppProps, IAppState> {

	state: IAppState = {
		error: null,
	};

	static getDerivedStateFromError() {
		return {
			error: EError.APP_ERROR,
		};
	}

	static getDerivedStateFromProps(nextProps: Readonly<IAppProps>, prevState: Readonly<IAppState>) {
		const {
			isError403,
			isError404,
			isError500,
			isError502,
		} = getConfig();
		const { error: prevError } = prevState;

		if (prevError === EError.APP_ERROR) {
			return null;
		}

		const error = (isError403 && EError.FORBIDDEN) ||
			(isError404 && EError.PAGE_NOT_FOUND) ||
			(isError500 && EError.INTERNAL_SERVER_ERROR) ||
			(isError502 && EError.BAD_GATEWAY) ||
			null
		;

		if (error) {
			Log.error(`Error: ${error}`);
		}

		return {
			error,
		};
	}

	componentDidMount(): void {
		const { location: { pathname, search } } = this.props;

		AppStat.routeData = { url: pathname };

		WindowVisibility.init();
		WindowVisibility.onChange(this.onVisibilityChange);

		Log.message(`Start app on ${pathname}?${search}`);
	}

	componentWillUnmount(): void {
		WindowVisibility.destroy();
	}

	componentDidUpdate(prevProps: Readonly<IAppProps>, prevState: Readonly<IAppState>): void {
		const {
			location: {
				pathname,
				search,
			},
		} = this.props;
		const {
			location: {
				pathname: prevPathname,
			},
		} = prevProps;
		const { error } = this.state;
		const { error: prevError } = prevState;

		if (pathname !== prevPathname) {
			if (error === EError.APP_ERROR && error === prevError) {
				this.clearError();
			}

			AppStat.routeData = { url: pathname };

			const isServerError = [
				EError.FORBIDDEN,
				EError.PAGE_NOT_FOUND,
				EError.INTERNAL_SERVER_ERROR,
				EError.BAD_GATEWAY,
			].includes(error as EError);

			if (isServerError && error === prevError) {
				this.clearServerError();
			}

			Log.message(`Navigate to ${pathname}?${search}`);
		}
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		// intentionally left blank: default React logger is ok by now
	}

	clearError() {
		this.setState({ error: null });
	}

	clearServerError() {
		setConfig({
			...getConfig(),
			isError: false,
			isError403: false,
			isError404: false,
			isError500: false,
			isError502: false,
		});

		this.clearError();
	}

	onVisibilityChange = () => {
		// if (WindowVisibility.isVisible()) {
		// 	this.setTrackInterval();
		// } else {
		// 	this.clearTrackInterval();
		// }
	};

	render() {
		let { error } = this.state;
		const errorCodes = [400, 403, 404];

		if (!error) {
			const {
				albumsStore: { itemShareNetworkStatus: { error: albumsShareError } },
				artistsStore: { itemShareNetworkStatus: { error: artistsShareError } },
				playlistsStore: { itemShareNetworkStatus: { error: playlistsShareError } },
				tracksStore: { itemShareNetworkStatus: { error: tracksShareError } },
				usersStore: { itemShareNetworkStatus: { error: usersShareError } },
			} = this.props;

			if (
				errorCodes.includes(albumsShareError?.status) ||
				errorCodes.includes(artistsShareError?.status) ||
				errorCodes.includes(playlistsShareError?.status) ||
				errorCodes.includes(tracksShareError?.status) ||
				errorCodes.includes(usersShareError?.status)
			) {
				error = EError.PAGE_NOT_FOUND;
			}
		}

		return this.props.render({ error });
	}

}

export const App = AppComponent as React.ComponentType<IAppOwnProps>;
